import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Handshake, Check, X, Building2, Pause, Play, Ban } from "lucide-react";

const ST = {
  pendente: { l: "Solicitada", c: "bg-aj-amber-soft text-aj-orange-hover" },
  solicitada: { l: "Solicitada", c: "bg-aj-amber-soft text-aj-orange-hover" },
  aceita: { l: "Ativa", c: "bg-aj-success text-white" },
  ativa: { l: "Ativa", c: "bg-aj-success text-white" },
  recusada: { l: "Recusada", c: "bg-[#E60000] text-white" },
  pausada: { l: "Pausada", c: "bg-[#F4F4F5] text-aj-text" },
  encerramento_programado: { l: "Encerramento em 30 dias", c: "bg-[#FFF7E0] text-[#B45309]" },
  encerrada: { l: "Encerrada", c: "bg-[#334155] text-white" },
};

export default function Parcerias() {
  const { user } = useAuth();
  const [docs, setDocs] = useState(null);
  const [resposta, setResposta] = useState({});
  const isMontadora = user?.role === "montadora";

  const load = useCallback(() => {
    api.get("/empresa/parcerias").then(r => setDocs(r.data)).catch(e => toast.error(formatApiError(e)));
  }, []);
  useEffect(load, [load]);

  const responder = async (p, acao) => {
    try {
      await api.post(`/parcerias/${p.id}/responder`, { acao, resposta: resposta[p.id] || null });
      toast.success(acao === "aceitar" ? "Parceria ativa! A empresa foi avisada." : "Solicitação recusada.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const alterarStatus = async (p, acao) => {
    try {
      await api.post(`/parcerias/${p.id}/status`, { acao, motivo: resposta[p.id] || null });
      toast.success(`Parceria ${acao === "pausar" ? "pausada" : acao === "reativar" ? "reativada" : "encerrada"}.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="parcerias-page">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange">Rede</div>
        <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-aj-blue-deep mt-1">Parcerias</h1>
        <p className="text-sm text-aj-text-muted mt-1 max-w-2xl">
          {isMontadora
            ? "Solicitações de parceria enviadas por empresas. Apenas parcerias ATIVAS participam da distribuição automática."
            : "Parcerias com montadoras e a situação de cada uma. Apenas parcerias ATIVAS participam da distribuição automática."}
        </p>
      </div>

      <div className="space-y-3">
        {docs?.length === 0 && (
          <div className="aj-card p-10 text-center text-aj-text-muted" data-testid="parcerias-vazio">
            <Handshake className="w-8 h-8 mx-auto mb-2 opacity-40" />
            Nenhuma solicitação de parceria por enquanto.
          </div>
        )}
        {docs?.map((p) => {
          const st = ST[p.status] || ST.pendente;
          const ativa = ["ativa", "aceita"].includes(p.status);
          const podeGerir = ativa || p.status === "pausada";
          return (
            <div key={p.id} className={`aj-card p-5 ${p.status === "pendente" ? "border-l-4 border-l-[#FF6700]" : ""}`}
              data-testid={`parceria-card-${p.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Building2 size={16} className="text-aj-orange" />
                    <span className="font-display font-bold text-aj-blue-deep">{p.empresa_nome || "Empresa"}</span>
                    <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${st.c}`}
                      data-testid={`parceria-status-${p.id}`}>{st.l}</span>
                  </div>
                  <div className="text-xs text-aj-text-muted mt-1">
                    Enviada em {new Date(p.created_at).toLocaleString("pt-BR")}
                    {p.volume_estimado ? ` · volume estimado: ${p.volume_estimado}/mês` : ""}
                  </div>
                  {p.mensagem && <p className="text-sm text-aj-text mt-2">"{p.mensagem}"</p>}
                  {p.resposta && (
                    <p className="text-sm text-aj-text-muted mt-1">Resposta da montadora: "{p.resposta}"</p>
                  )}
                  {p.status_motivo && (
                    <p className="text-sm text-aj-text-muted mt-1">Motivo: "{p.status_motivo}"</p>
                  )}
                </div>
              </div>
              {isMontadora && p.status === "pendente" && (
                <div className="mt-4 pt-4 border-t border-[#E2E8F0]">
                  <Textarea rows={2} placeholder="Mensagem para a empresa (opcional)"
                    value={resposta[p.id] || ""}
                    onChange={(e) => setResposta(r => ({ ...r, [p.id]: e.target.value }))}
                    data-testid={`parceria-resposta-${p.id}`} />
                  <div className="flex gap-2 mt-3">
                    <Button onClick={() => responder(p, "aceitar")}
                      className="flex-1 bg-aj-success hover:opacity-90 text-white"
                      data-testid={`parceria-aceitar-${p.id}`}>
                      <Check size={15} /> Aceitar parceria
                    </Button>
                    <Button onClick={() => responder(p, "recusar")} variant="outline"
                      className="flex-1 border-[#E60000] text-[#E60000] hover:bg-red-50"
                      data-testid={`parceria-recusar-${p.id}`}>
                      <X size={15} /> Recusar
                    </Button>
                  </div>
                </div>
              )}
              {podeGerir && (
                <div className="mt-4 pt-4 border-t border-[#E2E8F0] flex gap-2 flex-wrap">
                  {ativa && (
                    <Button size="sm" variant="outline" onClick={() => alterarStatus(p, "pausar")}
                      data-testid={`parceria-pausar-${p.id}`}>
                      <Pause size={14} /> Pausar
                    </Button>
                  )}
                  {p.status === "pausada" && (
                    <Button size="sm" className="bg-aj-success hover:opacity-90 text-white"
                      onClick={() => alterarStatus(p, "reativar")} data-testid={`parceria-reativar-${p.id}`}>
                      <Play size={14} /> Reativar
                    </Button>
                  )}
                  <Button size="sm" variant="outline"
                    className="border-[#E60000] text-[#E60000] hover:bg-red-50"
                    onClick={async () => {
                      if (user?.role === "empresa") {
                        if (!window.confirm("Encerrar a parceria com esta montadora? O encerramento só passa a valer 30 dias após este comunicado (registrado e informado à montadora).")) return;
                        try {
                          const r = await api.post(`/empresa/parcerias/${p.id}/encerrar`);
                          toast.success(`Encerramento comunicado. Válido a partir de ${new Date(r.data.efetivo_em).toLocaleDateString("pt-BR")}.`);
                          load();
                        } catch (e2) { toast.error(formatApiError(e2)); }
                      } else {
                        alterarStatus(p, "encerrar");
                      }
                    }} data-testid={`parceria-encerrar-${p.id}`}>
                    <Ban size={14} /> {user?.role === "empresa" ? "Encerrar (aviso de 30 dias)" : "Encerrar"}
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
